import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { fmtMoney, fmtDate } from '../lib/format';
import { paidTotal, balanceOf, displayLabel, expiryPhrase } from '../lib/ledger';
import EarningsChart from '../components/EarningsChart';

// ═══════════════════════════════════════════════════════════════════════════
//  VENDOR OVERVIEW — the screen a pro lands on
//
//  The portal had no landing screen at all: signing in dropped you straight
//  into a list of jobs, which on day one is empty. A tech we are asking to
//  trust us with their licence and their livelihood saw a blank list.
//
//  ── EVERY NUMBER ON THIS PAGE IS THIS VENDOR'S OWN RECORD ─────────────────
//  Nothing here is illustrative, rounded, or seeded. Each figure traces to a
//  document in the database:
//
//    Completed jobs   requests at completed / invoiced / paid
//    Active jobs      requests at assigned / in-progress
//    Paid to you      the sum of payments recorded against their invoices
//    Awaiting payment the unpaid balance on invoices they have submitted
//    Earnings chart   payments, by the month they were received
//    Verification     the compliance verdict the server computes from the
//                     expiry dates on their uploaded documents
//
//  Two things the design called for are NOT here, because the platform does
//  not measure them: an on-time rate and a star rating. Nothing records a
//  promised date against an actual one, and no customer has ever been asked to
//  rate anybody. A tech who saw "98% on-time" on their first day would learn,
//  correctly, that the numbers in this portal are decoration. Both are worth
//  building; neither is worth faking.
// ═══════════════════════════════════════════════════════════════════════════

const MONTHS_SHOWN = 6;

// Payments received, bucketed into the last six calendar months. Built from a
// fixed month list rather than from the payments themselves so a quiet month
// draws as a zero instead of vanishing and pulling the next month left.
function monthlyEarnings(ledger) {
  const now = new Date();
  const buckets = [];
  for (let i = MONTHS_SHOWN - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleDateString(undefined, { month: 'short' }),
      long: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      total: 0,
    });
  }
  const index = new Map(buckets.map(b => [b.key, b]));
  (ledger || []).forEach(entry => {
    (entry.payments || []).forEach(p => {
      const when = new Date(p.date || p.recordedAt || entry.createdAt);
      if (isNaN(when)) return;
      const bucket = index.get(`${when.getFullYear()}-${when.getMonth()}`);
      if (bucket) bucket.total += Number(p.amount) || 0;
    });
  });
  return buckets;
}

const CHECK = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12.5l5.5 5.5L20 6.5" />
  </svg>
);

function Metric({ label, value, sub, tone, badge }) {
  return (
    <div className="qps-metric-card">
      <div className="qps-metric-label">{label}</div>
      <div className="qps-metric-flex">
        <span className={`qps-metric-value${tone ? ' ' + tone : ''}`}>{value}</span>
        {badge && <span className="qps-badge-success" aria-hidden="true">{CHECK}</span>}
      </div>
      {sub && <div className="qps-metric-sub">{sub}</div>}
    </div>
  );
}

export default function VendorOverview() {
  const {
    currentUser, vendorProfile, requests, ledger, estimates, setVendorView,
  } = useApp();

  const compliance = vendorProfile?.compliance;
  const profile = vendorProfile?.profile || {};

  const active = (requests || []).filter(r => ['assigned', 'in-progress'].includes(r.status));
  const done = (requests || []).filter(r => ['completed', 'invoiced', 'paid'].includes(r.status));

  const months = useMemo(() => monthlyEarnings(ledger), [ledger]);

  const received = (ledger || []).reduce((s, e) => s + paidTotal(e), 0);
  const owed = (ledger || []).reduce((s, e) => s + Math.max(0, balanceOf(e)), 0);

  // This month against last. Only shown once there is a previous month with
  // money in it — "up 100%" from nothing is not a fact about the business.
  const thisMonth = months[months.length - 1]?.total || 0;
  const lastMonth = months[months.length - 2]?.total || 0;
  const change = lastMonth > 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100) : null;

  const waiting = (estimates || []).filter(e => e.status === 'submitted');
  const trades = [...(profile.expertise || []), profile.expertiseOther].filter(Boolean);

  // The checklist that decides whether this vendor can be sent work and paid.
  // Straight off the server's compliance verdict, so what the tech reads here
  // is the same rule the payment gate applies.
  const steps = [
    {
      label: 'Application submitted',
      done: Boolean(profile.completedAt),
      note: profile.completedAt ? fmtDate(profile.completedAt) : 'Not finished',
    },
    ...((compliance?.items || []).map(item => ({
      label: displayLabel(item),
      done: item.status === 'valid',
      warn: item.status === 'expiring',
      note: item.status === 'missing' ? 'Not on file' : `Expires ${expiryPhrase(item.daysLeft)}`,
    }))),
  ];
  const stepsDone = steps.filter(s => s.done).length;

  // Real events off real records, newest first. No feed table exists, so this
  // is assembled from what already happened rather than invented as a
  // notification stream.
  const activity = useMemo(() => {
    const out = [];
    (ledger || []).forEach(e => (e.payments || []).forEach(p => out.push({
      when: p.date || p.recordedAt,
      title: `Payment received · ${fmtMoney(p.amount)}`,
      body: e.serviceAddress || e.jobDetails || 'Invoice payment',
    })));
    (estimates || []).forEach(e => {
      if (e.status === 'approved') out.push({
        when: e.approvedAt || e.updatedAt,
        title: 'Estimate approved',
        body: e.serviceAddress || e.trade || 'Your estimate was accepted',
      });
    });
    (requests || []).filter(r => r.status === 'assigned').forEach(r => out.push({
      when: r.assignedAt || r.updatedAt || r.createdAt,
      title: 'Job assigned to you',
      body: r.serviceAddress || r.serviceType || 'New work',
    }));
    return out
      .filter(a => a.when && !isNaN(new Date(a.when)))
      .sort((a, b) => new Date(b.when) - new Date(a.when))
      .slice(0, 4);
  }, [ledger, estimates, requests]);

  const verified = compliance?.status === 'valid';

  return (
    <div className="qps-portal">
      <div className="qps-portal-container">

        <main className="qps-main-panel">
          <div className="qps-panel-meta">qpshub.com vendor portal</div>
          <h1 className="qps-main-heading">
            {vendorProfile?.businessName || currentUser?.name || 'Your business'}
          </h1>
          <p className="qps-main-lede">
            {trades.length > 0
              ? trades.join(' · ')
              : 'Add your trades from the application so work starts routing to you.'}
          </p>

          {/* Three cards, as the spec draws them. The spec's other two —
              on-time rate and a star rating — are not here: nothing in this
              system records a promised date against an actual one, and no
              customer has ever been asked to rate anybody. These three are
              this vendor's own records. */}
          <section className="qps-metrics-grid">
            <Metric
              label="Completed jobs"
              value={done.length}
              badge={done.length > 0}
              sub={done.length === 0 ? 'Your record starts with the first one' : 'Finished and signed off'}
            />
            <Metric
              label="Paid to you"
              value={fmtMoney(received)}
              badge={received > 0}
              sub="All time"
            />
            <Metric
              label="Awaiting payment"
              value={fmtMoney(owed)}
              tone={owed > 0 ? 'warn' : undefined}
              sub={owed > 0 ? 'Invoiced, not yet settled' : 'Nothing outstanding'}
            />
          </section>

          <section className="qps-dashboard-grid">

            <div className="qps-dashboard-card">
              <div className="qps-card-header-row">
                <div>
                  <h3 className="qps-card-title">Earnings</h3>
                  <p className="qps-card-subtitle">Money received, by month</p>
                </div>
                <div className="qps-earnings-total">
                  <span className="qps-earnings-label">Total received</span>
                  <span className="qps-earnings-amount">
                    {fmtMoney(received)}
                    {change !== null && (
                      <small className={change >= 0 ? 'up' : 'down'}>
                        {change >= 0 ? '▲' : '▼'} {Math.abs(change)}%
                      </small>
                    )}
                  </span>
                </div>
              </div>

              {received > 0 ? (
                <EarningsChart months={months} />
              ) : (
                <div className="qps-card-empty">
                  Nothing paid out yet. This fills in as invoices get settled,
                  one point per month.
                </div>
              )}
            </div>

            <div className="qps-dashboard-card">
              <h3 className="qps-card-title">Active jobs</h3>
              <p className="qps-card-subtitle">Assigned to you right now</p>

              {active.length === 0 ? (
                <div className="qps-card-empty" style={{ marginTop: 14 }}>
                  No work assigned yet. Jobs route by trade and travel radius,
                  so the fastest thing you can do is finish your paperwork.
                </div>
              ) : (
                <div className="qps-table-wrapper">
                <table className="qps-data-table">
                  <thead>
                    <tr><th>Job</th><th>Where</th><th>Status</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {active.slice(0, 6).map(r => (
                      <tr key={r._id}>
                        <td className="qps-td-strong">{r.serviceType || 'Work'}</td>
                        <td className="qps-text-truncate">{r.serviceAddress || '—'}</td>
                        <td>
                          <span className={`qps-status-pill ${r.status === 'in-progress' ? 'qps-pill-progress' : 'qps-pill-new'}`}>
                            {r.status === 'in-progress' ? 'IN PROGRESS' : 'ASSIGNED'}
                          </span>
                        </td>
                        <td>
                          <button type="button" className="qps-btn-action" onClick={() => setVendorView('jobs')}>Manage</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}

              {/* The spec puts "Available Leads" here. There is no open lead
                  pool on this platform — admin assigns work to a vendor, and
                  a vendor cannot browse and claim it — so those rows would be
                  a button that does nothing. This is the nearest real thing:
                  what the tech has sent us and is waiting on. */}
              <h3 className="qps-card-title qps-section-divider">Estimates waiting on us</h3>
              {waiting.length === 0 ? (
                <div className="qps-card-empty tight">
                  Nothing submitted. Price a job from the Estimates tab and it
                  lands here until we approve it.
                </div>
              ) : waiting.slice(0, 3).map(e => (
                <div key={e._id} className="qps-lead-row">
                  <span>
                    <b>{e.serviceAddress || e.trade || 'Estimate'}</b>
                    <i>{fmtMoney(e.techTotal)} · sent {fmtDate(e.submittedAt || e.createdAt)}</i>
                  </span>
                  <button type="button" className="qps-btn-secondary" onClick={() => setVendorView('estimates')}>Details</button>
                </div>
              ))}
            </div>

          </section>
        </main>

        <aside className="qps-sidebar-panel">

          <div className="qps-sidebar-block">
            <h4 className="qps-sidebar-heading">Contractor profile</h4>
            <p className="qps-sidebar-sub">What clients see</p>

            <div className={`qps-badge-item${verified ? '' : ' off'}`}>
              <span className={`qps-dot-icon${verified ? ' on' : ''}`}>{verified ? CHECK : ''}</span>
              <span style={{ flex: 1 }}>
                <b>Verified Pro</b>
                <i>{verified ? 'Licence and insurance current' : 'Needs current licence and insurance'}</i>
              </span>
            </div>

            <div className={`qps-badge-item${done.length > 0 ? '' : ' off'}`}>
              <span className={`qps-dot-icon${done.length > 0 ? ' on' : ''}`}>{done.length > 0 ? CHECK : ''}</span>
              <span style={{ flex: 1 }}>
                <b>{done.length} job{done.length === 1 ? '' : 's'} finished</b>
                <i>{done.length === 0 ? 'Your record starts with the first one' : 'Shown on your public profile'}</i>
              </span>
            </div>

            <div className={`qps-badge-item${trades.length > 0 ? '' : ' off'}`}>
              <span className={`qps-dot-icon${trades.length > 0 ? ' on' : ''}`}>{trades.length > 0 ? CHECK : ''}</span>
              <span style={{ flex: 1 }}>
                <b>{trades.length} trade{trades.length === 1 ? '' : 's'} listed</b>
                <i>{trades.length === 0 ? 'Pick your trades to start matching' : 'Work routes to you on these'}</i>
              </span>
            </div>
          </div>

          <div className="qps-sidebar-block">
            <h4 className="qps-sidebar-heading">Onboarding journey</h4>
            <p className="qps-sidebar-sub">{stepsDone} of {steps.length} done</p>

            <div className="qps-progress"><span style={{ width: `${(stepsDone / steps.length) * 100}%` }} /></div>

            {steps.map((s, i) => (
              <div key={i} className={`qps-task-row${s.done ? ' done' : s.warn ? ' warn' : ''}`}>
                <span style={{ flex: 1 }}>
                  <b>{s.label}</b>
                  <i>{s.note}</i>
                </span>
                <span className="qps-task-check">{s.done ? CHECK : s.warn ? '!' : ''}</span>
              </div>
            ))}

            {stepsDone < steps.length && (
              <button type="button" className="qps-btn-primary qps-sidebar-cta" onClick={() => setVendorView('docs')}>
                Upload documents
              </button>
            )}
          </div>

          <div className="qps-sidebar-block">
            <h4 className="qps-sidebar-heading">Recent activity</h4>
            <p className="qps-sidebar-sub">On your account</p>
            {activity.length === 0 ? (
              <div className="qps-card-empty tight">Jobs, approvals and payments show up here.</div>
            ) : activity.map((a, i) => (
              <div key={i} className="qps-alert-item">
                <div className="qps-alert-meta">
                  <span>{a.title}</span>
                  <span>{fmtDate(a.when)}</span>
                </div>
                <p className="qps-alert-body">{a.body}</p>
              </div>
            ))}
          </div>

        </aside>

      </div>
    </div>
  );
}
